/**
 * lmsr.js — Port JavaScript du moteur LMSR backend.
 *
 * Utilisé pour calculer le coût d'un trade EN LOCAL pendant que l'utilisateur
 * tape (preview instantanée, pas de round-trip réseau).
 *
 * Le backend recalcule ensuite avec exactement les mêmes formules pour
 * confirmer le prix au moment de l'exécution.
 *
 * Math :
 *   C(q)    = b · ln( Σ exp(q_i / b) )
 *   Price_i = exp(q_i / b) / Σ exp(q_j / b)
 */

/** log(Σ exp(x_i)) avec shift pour stabilité numérique. */
function logSumExp(xs) {
  const m = Math.max(...xs);
  return m + Math.log(xs.reduce((acc, x) => acc + Math.exp(x - m), 0));
}

/** Coût LMSR total : C(q) = b · ln(Σ exp(q_i / b)). */
export function cost(qs, b) {
  return b * logSumExp(qs.map(q => q / b));
}

/** Prix (= probabilités) actuels pour chaque outcome. Somme = 1. */
export function prices(qs, b) {
  const xs = qs.map(q => q / b);
  const m = Math.max(...xs);
  const exps = xs.map(x => Math.exp(x - m));
  const sum = exps.reduce((a, x) => a + x, 0);
  return exps.map(e => e / sum);
}

/** Prix actuel d'un seul outcome. */
export function priceOne(qs, b, idx) {
  return prices(qs, b)[idx];
}

/**
 * Coût (positif) pour acheter `delta` parts de l'outcome `idx`.
 * Pour une vente, passer delta négatif → retour négatif = revenu reçu.
 */
export function costToTrade(qs, b, idx, delta) {
  const qsNew = [...qs];
  qsNew[idx] = qsNew[idx] + delta;
  return cost(qsNew, b) - cost(qs, b);
}

/**
 * Prix moyen effectif d'un trade = |cost| / |delta|.
 * C'est le prix réellement payé par share (avec slippage inclus).
 */
export function effectivePrice(qs, b, idx, delta) {
  if (delta === 0) return 0;
  const c = costToTrade(qs, b, idx, delta);
  return Math.abs(c) / Math.abs(delta);
}

/**
 * Calcule le pourcentage de slippage d'un trade.
 * Slippage = (effective_price − current_price) / current_price × 100
 * Positif pour BUY (paie plus que le prix actuel),
 * positif pour SELL (reçoit moins que le prix actuel) — toujours retourné en absolu.
 */
export function slippagePct(qs, b, idx, delta) {
  if (delta === 0) return 0;
  const curr = priceOne(qs, b, idx);
  const eff  = effectivePrice(qs, b, idx, delta);
  if (curr === 0) return 0;
  return Math.abs((eff - curr) / curr) * 100;
}
